import { assign, createMachine, Interpreter } from 'xstate-v4'
import { isDayPast } from '~/utility/date/relative'
import { getInitialContext } from './context'
import { BookingContext, BookingStateEvent } from './types'
import { userMachineConfig } from './user'

/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access,
   @typescript-eslint/no-unsafe-return -- TODO: Properly type XState event.data payloads */
export function returnBookingMachine() {
  return createMachine<BookingContext, BookingStateEvent>(
    {
      id: 'booking',
      initial: 'setInitialData',
      context: getInitialContext(),
      states: {
        setInitialData: {
          invoke: {
            src: 'setInitialData',
            onDone: {
              actions: ['setInitialExtras'],
              target: 'loadingData',
            },
          },
        },
        loadingData: {
          invoke: {
            src: 'loadData',
            onDone: [
              {
                target: 'requestError',
                actions: assign({
                  details: (_, { data }) => data.info,
                }),
                cond: ({ requestParams }, { data }) =>
                  !requestParams.stayRange.start ||
                  !requestParams.stayRange.end ||
                  isDayPast(requestParams.stayRange.start) ||
                  isDayPast(requestParams.stayRange.end) ||
                  data.quote === null,
              },
              {
                target: 'validateInitialData',
                actions: assign({
                  details: (_, { data }) => data.info,
                  quote: (_, { data }) => data.quote,
                  stripeToken: (_, { data }) => data.stripeToken,
                }),
              },
            ],
            onError: [
              {
                target: 'campsiteNotFoundError',
                actions: [
                  assign({
                    details: (_, { data }) => data.info,
                  }),
                  'logError',
                ],
                // check every attribute as data can be anything
                // we don't want to cause an error in onErroor
                cond: (_, { data }) =>
                  data && data.response && data.response.status === 404,
              },
              {
                target: 'loadingError',
                actions: ['logError', 'decrementRetryCounter'],
              },
            ],
          },
        },
        loadingQuote: {
          invoke: {
            src: 'loadQuote',
            onDone: [
              {
                target: 'validateInitialData',
                actions: assign({
                  quote: (_, { data }) => data,
                }),
                cond: (_, { data }) => data !== null,
              },
              {
                target: 'requestError',
              },
            ],
            onError: [
              {
                target: 'loadingError',
                actions: ['logError', 'decrementRetryCounter'],
              },
            ],
          },
        },
        validateInitialData: {
          invoke: {
            src: 'validateInitialData',
            onDone: {
              target: 'dataEntry',
              actions: ['setInitialExtras'],
            },
            onError: [{ target: 'loadingError', actions: 'logError' }],
          },
        },
        settingExtras: {
          entry: ['setExtras'],
          on: {
            '': 'loadingQuote',
          },
        },
        dataEntry: {
          on: {
            EXTRAS_CHANGED: {
              target: 'settingExtras',
            },
            SUBMIT: {
              actions: ['setBookingRequest'],
              target: 'submitting',
            },
            UPDATE_CONTACT_DETAILS: {
              actions: assign({
                contactDetails: (_, { details }) => details,
              }),
              internal: true,
            },
            UPDATE_OTHER_DETAILS: {
              actions: assign({
                otherDetails: (_, { details }) => details,
              }),
              internal: true,
            },
            EMAIL_CHANGED: {
              internal: false,
              target: '.data.emailChanged',
            },
          },
          ...userMachineConfig,
        },
        submitting: {
          invoke: {
            src: 'submitForm',
            onDone: [
              {
                target: 'paypalProcessing',
                actions: 'setTaskIdAndBookingId',
                cond: ({ bookingRequest }) => {
                  return bookingRequest?.paymentDetails.method === 'paypal'
                },
              },
              {
                target: 'processing',
                actions: 'setTaskIdAndBookingId',
              },
            ],
            onError: [
              {
                target: 'alreadyBooked',
                cond: (_, { data }: any) => !!data?.alreadyBooked,
              },
              {
                target: 'allFormError',
                cond: (_, { data }: any) => {
                  return !!data?.formErrors?.__all__
                },
              },
              {
                target: 'dataEntry',
                cond: (_, { data }: any) => !!data?.formErrors,
                actions: assign({
                  submitErrors: (_, { data }: any) => data.formErrors,
                }),
              },
              {
                target: 'unknownError',
                cond: (_, { data }: any) => !!data?.unknownError,
              },
              {
                target: 'submitServerError',
              },
            ],
          },
        },
        requestError: {
          type: 'final',
        },
        campsiteNotFoundError: {
          type: 'final',
        },
        paypalProcessing: {
          on: {
            PAYPAL_CANCELLED: {
              target: 'dataEntry',
            },
            PAYPAL_ERROR: {
              target: 'dataEntry',
            },
            PAYPAL_PAID: {
              target: 'paypalPaid',
            },
          },
        },
        paypalPaid: {
          type: 'final',
        },
        loadingError: {
          entry: assign({
            loadingErrors: (_, { data }: any) => data,
          }),
          after: {
            5000: [
              {
                target: 'loadingData',

                cond: ({ retries_remain }) => retries_remain >= 0,
              },
              {
                target: 'requestError',
              },
            ],
          },
        },
        // moves to processing page with it's own machine
        processing: {
          type: 'final',
        },
        alreadyBooked: {
          type: 'final',
          entry: assign({
            error: (_, { data }: any) => data.alreadyBooked.message,
          }),
        },
        allFormError: {
          type: 'final',
          entry: assign({
            error: (_, { data }: any) => data.formErrors.__all__[0],
            submitErrors: (_, { data }: any) => data.formErrors,
          }),
        },
        unknownError: {
          type: 'final',
          entry: [
            assign({
              error: (_) => 'unknown error',
            }),
            'logUnknownError',
          ],
        },
        submitServerError: {
          entry: [
            'logError',
            assign({ submitErrors: (_, { data }: any) => data }),
          ],
          after: {
            1: 'dataEntry',
          },
        },
      },
    },
    {
      services: {
        validateInitialData: async ({ quote, selectedExtras }) => {
          const correctExtras = { ...selectedExtras }
          Object.keys(selectedExtras).forEach((extra) => {
            const quoteExtraData = quote!.extras.find((e) => e.id === extra)
            if (!quoteExtraData) {
              delete correctExtras[extra]
            }
            if (quoteExtraData && correctExtras[extra] > quoteExtraData.max) {
              correctExtras[extra] = quoteExtraData.max
            }
          })
          return await Promise.resolve(correctExtras)
        },
        setInitialData: async ({ requestParams }) => {
          return await Promise.resolve(requestParams.selectedExtras)
        },
      },
      actions: {
        setBookingRequest: assign(
          ({ contactDetails, otherDetails, selectedExtras }, event): object => {
            const bookingRequest =
              event.type === 'SUBMIT'
                ? {
                    ...event.payload,
                    contactDetails,
                    otherDetails,
                    selectedExtras,
                  }
                : undefined
            return { bookingRequest }
          },
        ),
        setInitialExtras: assign((_, { data }: any) => {
          return { selectedExtras: data }
        }),
        setExtras: assign(({ selectedExtras, quote }, event) => {
          if (event.type !== 'EXTRAS_CHANGED') {
            throw new Error('Wrong event')
          }
          const extras = { ...selectedExtras }
          quote?.extras.forEach((e) => {
            if (e.isCompulsory) {
              delete extras[e.id]
            } else if (!extras[e.id]) {
              extras[e.id] = 0
            }
          })
          event.selected.forEach((selectedExtra) => {
            extras[selectedExtra.extraId] = selectedExtra.quantity
          })
          return { selectedExtras: extras }
        }),
        setTaskIdAndBookingId: assign((_, { data }: any) => ({
          taskId: data.taskId,
          bookingId: data.bookingId,
        })),
        setEmail: assign((_, e) => ({
          email: e.type === 'EMAIL_CHANGED' ? e.email : '',
        })),

        decrementRetryCounter: assign(({ retries_remain }) => ({
          retries_remain: retries_remain - 1,
        })),
        logError: () => {
          throw new Error('authMachine logError not implemented')
        },
      },
      guards: {
        hasEmail: ({ email }) => email.length > 3,
        notLoggedInAndHasEmail: ({ isAuthenticated, email }) =>
          !isAuthenticated && email.length > 3,
      },
    },
  )
}

export type BookingService = Interpreter<BookingContext, any, BookingStateEvent>
